import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import accountApi from '../../services/accountApi';

function PlatformTeamPage() {
  const [catalog, setCatalog] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantRole, setGrantRole] = useState('');
  const [revokeUserId, setRevokeUserId] = useState('');
  const [revokeRole, setRevokeRole] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [catalogData, assignmentRows] = await Promise.all([
        accountApi.getPlatformRoleCatalog(),
        accountApi.listPlatformRoleAssignments(),
      ]);
      setCatalog(catalogData);
      setAssignments(assignmentRows);
      const assignable = (catalogData && catalogData.assignable_roles) || [];
      setGrantRole((current) => current || (assignable[0] ?? ''));
      setRevokeRole((current) => current || (assignable[0] ?? ''));
    } catch (err) {
      setError(err.message || 'Failed to load platform team');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const assignableRoles = (catalog && catalog.assignable_roles) || [];
  const canManage = assignableRoles.length > 0;

  const handleGrant = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    setSubmitting(true);
    setError(null);
    try {
      await accountApi.grantPlatformRole({ email: grantEmail, role: grantRole });
      setGrantEmail('');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to grant role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    setSubmitting(true);
    setError(null);
    try {
      await accountApi.revokePlatformRole({
        user_id: Number(revokeUserId),
        role: revokeRole,
      });
      setRevokeUserId('');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to revoke role');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h1 className="product-page-title">Platform team</h1>
      <p className="product-page-subtitle">Platform role assignments and catalog.</p>

      {error && <Alert variant="danger">{error}</Alert>}
      {loading && (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading…
        </div>
      )}

      {!loading && catalog && (
        <>
          <div className="product-card mb-3">
            <p className="mb-0 small text-muted">
              Your platform roles:{' '}
              <strong>{(catalog.current_roles || []).join(', ') || '—'}</strong>
            </p>
          </div>

          <div className="product-card mb-3">
            <h2 className="h6 mb-3">Role catalog</h2>
            <Table responsive className="product-table" size="sm">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Description</th>
                  <th>Permissions</th>
                </tr>
              </thead>
              <tbody>
                {(catalog.roles || []).map((role) => (
                  <tr key={role.code}>
                    <td>
                      <strong>{role.display_name || role.code}</strong>
                      <div className="text-muted small">{role.code}</div>
                    </td>
                    <td>{role.description || '—'}</td>
                    <td>
                      <code className="small">{(role.permissions || []).join(', ')}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="product-card mb-3">
            <h2 className="h6 mb-3">Assignments</h2>
            <Table responsive className="product-table" size="sm">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Granted</th>
                  <th>Revoked</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((row) => (
                  <tr key={row.id}>
                    <td>{row.email}</td>
                    <td>{row.role}</td>
                    <td>{row.status}</td>
                    <td>{row.granted_at ? new Date(row.granted_at).toLocaleString() : '—'}</td>
                    <td>{row.revoked_at ? new Date(row.revoked_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {canManage && (
            <div className="row g-3">
              <div className="col-lg-6">
                <div className="product-card">
                  <h2 className="h6 mb-3">Grant role</h2>
                  <Form onSubmit={handleGrant} className="row g-2 align-items-end">
                    <div className="col-md-6">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={grantEmail}
                        onChange={(e) => setGrantEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Role</Form.Label>
                      <Form.Select
                        value={grantRole}
                        onChange={(e) => setGrantRole(e.target.value)}
                      >
                        {assignableRoles.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                    <div className="col-md-2">
                      <Button type="submit" disabled={submitting}>
                        Grant
                      </Button>
                    </div>
                  </Form>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="product-card">
                  <h2 className="h6 mb-3">Revoke role</h2>
                  <Form onSubmit={handleRevoke} className="row g-2 align-items-end">
                    <div className="col-md-4">
                      <Form.Label>User ID</Form.Label>
                      <Form.Control
                        type="number"
                        value={revokeUserId}
                        onChange={(e) => setRevokeUserId(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <Form.Label>Role</Form.Label>
                      <Form.Select
                        value={revokeRole}
                        onChange={(e) => setRevokeRole(e.target.value)}
                      >
                        {assignableRoles.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                    <div className="col-md-2">
                      <Button type="submit" variant="outline-danger" disabled={submitting}>
                        Revoke
                      </Button>
                    </div>
                  </Form>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PlatformTeamPage;
